/**
 * DeskResumeListMolecule — the Desk's "Resume" region: the caller's recently opened/saved
 * templates.
 *
 * Pre-create check (atomic-design Rule 3): `GlobalSearchResultsListMolecule` was considered and
 * rejected — it is typed to sphere-shaped results and renders sphere avatars/perspectives, and
 * generifying it is a breaking type change to the nav bar (see the spec's Reuse Audit). Each ROW's
 * parts are existing atoms (`ChipAtom`, `GlyphAtom`, `StatusBadgeAtom`, `EmptyStateAtom`); only the
 * `DeskTemplateRef`-specific composition is new here.
 */

import React from 'react';
import { useTranslation } from 'localization';
import { ChipAtom, GlyphAtom, StatusBadgeAtom, EmptyStateAtom, MessageAtom, formatRelativeTime } from 'design-system/my-lib';
import type { DeskTemplateRef } from 'types/studio';
import { studioRouteForTemplate } from './studioRouteForTemplate';
import './DeskResumeListMolecule.scss';

export interface DeskResumeListMoleculeProps {
  items: DeskTemplateRef[];
  /** True when the `recents` sub-source failed — items still render (empty), a quiet notice appends. */
  degraded?: boolean;
  onOpen: (item: DeskTemplateRef, route: string) => void;
  className?: string;
}

/**
 * Resume region (spec SC2/UI Components table) — rows: glyph, name, type chip, status chip,
 * relative time; click resumes the exact builder route. Route is resolved via
 * `studioRouteForTemplate` (the authoritative PWA source). When it returns `null` — no active
 * builder owns the type — the row renders NON-interactive rather than falling back to the
 * server-supplied `route`: that fallback hits the `path="*"` catch-all and bounces back to
 * `/studio`, a dead click dressed as a link (review pass 1). The row still shows the template
 * (it IS a real recent) with a "no builder yet" hint.
 */
const DeskResumeListMolecule: React.FC<DeskResumeListMoleculeProps> = ({
  items,
  degraded = false,
  onOpen,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div className={['desk-resume-list', className].filter(Boolean).join(' ')} data-testid="desk-resume-list">
      {degraded && (
        <div data-testid="desk-resume-degraded">
          <MessageAtom severity="warn" text={t('studio.desk.resume.degraded')} />
        </div>
      )}
      {items.length === 0 && !degraded && (
        <EmptyStateAtom
          icon="pi-history"
          title={t('studio.desk.resume.emptyTitle')}
          description={t('studio.desk.resume.emptyDescription')}
        />
      )}
      {items.length > 0 && (
        <ul className="desk-resume-list__items">
          {items.map((item) => {
            const route = studioRouteForTemplate(item);
            const rowContent = (
              <>
                <GlyphAtom glyph={item.glyph} fallbackText={item.displayName} />
                <span className="desk-resume-list__name">{item.displayName}</span>
                <ChipAtom
                  className="desk-resume-list__type-chip"
                  label={t(`studio.desk.templateType.${item.type}`, { defaultValue: item.type })}
                />
                <StatusBadgeAtom
                  tone={item.status === 'draft' ? 'warn' : 'success'}
                  label={t(`studio.desk.status.${item.status}`)}
                  size="small"
                />
                <span className="desk-resume-list__time">{formatRelativeTime(item.updatedAt, t)}</span>
              </>
            );
            return (
              <li key={item.templateId}>
                {route ? (
                  <button
                    type="button"
                    className="desk-resume-list__row"
                    data-testid={`desk-resume-row-${item.templateId}`}
                    onClick={() => onOpen(item, route)}
                  >
                    {rowContent}
                  </button>
                ) : (
                  <div
                    className="desk-resume-list__row desk-resume-list__row--no-builder"
                    data-testid={`desk-resume-row-${item.templateId}`}
                    title={t('studio.desk.resume.noBuilderYet')}
                  >
                    {rowContent}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DeskResumeListMolecule;
